<header class="header" role="banner">
  <div class="header__inner">
    <!-- Logo -->
    <a class="header__logo-link" routerLink="/" aria-label="CapX Flow home">
      <img
        class="header__logo"
        [src]="isDark() ? '/assets/images/logo-dark.png' : '/assets/images/logo.png'"
        alt="CapX Flow"
      />
    </a>

    <!-- Desktop primary nav (hidden ≤768px) -->
    <nav class="header__nav" aria-label="Primary navigation">
      @if (isAuthenticated()) {
        @for (link of navLinks; track link.path) {
          <a
            class="header__nav-link"
            [routerLink]="link.path"
            routerLinkActive="header__nav-link--active"
            ariaCurrentWhenActive="page"
            >{{ link.label }}</a
          >
        }

        <!-- Agent dropdown (agents only) -->
        @if (isAgent()) {
          <div class="header__menu">
            <button
              class="header__nav-link header__menu-toggle"
              type="button"
              [class.header__nav-link--active]="openMenu() === 'agent'"
              [attr.aria-expanded]="openMenu() === 'agent'"
              aria-haspopup="true"
              (click)="toggleRoleMenu('agent')"
            >
              Agent
              <app-icon name="chevron-down" [size]="16" />
            </button>
            @if (openMenu() === 'agent') {
              <div class="header__menu-panel" role="menu">
                @for (link of agentLinks(); track link.path) {
                  <a
                    class="header__menu-item"
                    role="menuitem"
                    [routerLink]="link.path"
                    routerLinkActive="header__menu-item--active"
                    >{{ link.label }}</a
                  >
                }
              </div>
            }
          </div>
        }

        <!-- Ops dropdown (ops staff only) -->
        @if (isOps()) {
          <div class="header__menu">
            <button
              class="header__nav-link header__menu-toggle"
              type="button"
              [class.header__nav-link--active]="openMenu() === 'ops'"
              [attr.aria-expanded]="openMenu() === 'ops'"
              aria-haspopup="true"
              (click)="toggleRoleMenu('ops')"
            >
              Ops
              <app-icon name="chevron-down" [size]="16" />
            </button>
            @if (openMenu() === 'ops') {
              <div class="header__menu-panel" role="menu">
                @for (link of opsLinks; track link.path) {
                  <a
                    class="header__menu-item"
                    role="menuitem"
                    [routerLink]="link.path"
                    routerLinkActive="header__menu-item--active"
                    >{{ link.label }}</a
                  >
                }
              </div>
            }
          </div>
        }

        <!-- Admin dropdown (system admins only) -->
        @if (isAdmin()) {
          <div class="header__menu">
            <button
              class="header__nav-link header__menu-toggle"
              type="button"
              [class.header__nav-link--active]="openMenu() === 'admin'"
              [attr.aria-expanded]="openMenu() === 'admin'"
              aria-haspopup="true"
              (click)="toggleRoleMenu('admin')"
            >
              Admin
              <app-icon name="chevron-down" [size]="16" />
            </button>
            @if (openMenu() === 'admin') {
              <div class="header__menu-panel" role="menu">
                @for (link of adminLinks; track link.path) {
                  <a
                    class="header__menu-item"
                    role="menuitem"
                    [routerLink]="link.path"
                    routerLinkActive="header__menu-item--active"
                    >{{ link.label }}</a
                  >
                }
              </div>
            }
          </div>
        }
      }
    </nav>

    <!-- Desktop right-side actions (hidden ≤768px) -->
    <div class="header__actions">
      <!-- Debug menu (local development only) -->
      @if (isDebugEnabled) {
        <div class="header__debug">
          <button
            class="header__debug-toggle"
            type="button"
            [attr.aria-expanded]="isDebugOpen()"
            aria-haspopup="true"
            (click)="toggleDebug()"
            matTooltip="Debug menu (local dev only)"
          >
            <app-icon name="bug" [size]="18" />
            Debug
          </button>
          @if (isDebugOpen()) {
            <div class="header__debug-menu" role="menu">
              <p class="header__debug-heading">Switch User</p>
              @for (g of devUserGroups(); track g.group; let first = $first) {
                <p
                  class="header__debug-group"
                  [class.header__debug-group--divided]="!first"
                  role="presentation"
                >
                  {{ g.heading }}
                </p>
                @for (u of g.users; track u.email) {
                  <button
                    class="header__debug-item"
                    type="button"
                    role="menuitem"
                    (click)="switchUser(u.email)"
                  >
                    <span class="header__debug-name">{{ u.first_name }} {{ u.last_name }}</span>
                    <span class="header__debug-meta">{{ u.role }} · {{ u.email }}</span>
                  </button>
                }
              } @empty {
                <!-- Three distinct states: in flight, failed, and genuinely empty.
                     They used to collapse into one "run make seed", which is wrong
                     advice on staging and misleading when the fetch never landed. -->
                @if (devUsersLoading()) {
                  <p class="header__debug-empty">Loading users…</p>
                } @else if (devUsersError()) {
                  <p class="header__debug-empty">
                    Couldn't load users — the dev routes may be off. Close and reopen this menu to
                    retry.
                  </p>
                } @else {
                  <p class="header__debug-empty">
                    No users found. Seed demo data from Admin → System Status, or run
                    <code>make seed</code> locally.
                  </p>
                }
              }
            </div>
          }
        </div>
      }
      <button
        class="header__theme-toggle"
        type="button"
        (click)="toggleTheme()"
        [attr.aria-label]="isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
        [attr.title]="isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
      >
        @if (isDark()) {
          <app-icon name="sun" [size]="20" />
        } @else {
          <app-icon name="moon" [size]="20" />
        }
      </button>
      @if (isAuthenticated()) {
        <!-- Account menu (person name → Profile / Log out) -->
        <div class="header__menu header__user">
          <button
            class="header__user-toggle"
            type="button"
            [class.header__user-toggle--active]="isUserMenuOpen()"
            [attr.aria-expanded]="isUserMenuOpen()"
            aria-haspopup="true"
            data-testid="user-menu-toggle"
            (click)="toggleUserMenu()"
          >
            <span class="header__user-avatar" aria-hidden="true">
              <app-icon name="user" [size]="18" />
            </span>
            <span class="header__user-name">{{ displayName() }}</span>
            <app-icon name="chevron-down" [size]="16" />
          </button>
          @if (isUserMenuOpen()) {
            <div class="header__menu-panel header__user-panel" role="menu">
              <a
                class="header__menu-item header__user-item"
                role="menuitem"
                routerLink="/profile"
                data-testid="user-menu-profile"
              >
                <app-icon name="user" [size]="16" />
                Profile
              </a>
              <button
                class="header__menu-item header__user-item"
                type="button"
                role="menuitem"
                data-testid="user-menu-logout"
                (click)="logout()"
              >
                <app-icon name="log-out" [size]="16" />
                Log out
              </button>
            </div>
          }
        </div>
      } @else {
        <a mat-stroked-button routerLink="/auth/login" class="header__login-btn">
          <app-icon name="log-in" [size]="16" />
          Log in
        </a>
        <a mat-flat-button color="primary" routerLink="/auth/register">Sign up</a>
      }
    </div>

    <!-- Hamburger button (visible ≤768px only) -->
    <button
      class="header__hamburger"
      type="button"
      [attr.aria-expanded]="isMenuOpen()"
      aria-controls="mobile-menu"
      [attr.aria-label]="isMenuOpen() ? 'Close navigation menu' : 'Open navigation menu'"
      (click)="toggleMenu()"
    >
      @if (isMenuOpen()) {
        <app-icon name="x" [size]="24" />
      } @else {
        <app-icon name="menu" [size]="24" />
      }
    </button>
  </div>

  <!-- Mobile menu panel -->
  <nav
    id="mobile-menu"
    class="header__mobile-menu"
    [class.header__mobile-menu--open]="isMenuOpen()"
    aria-label="Mobile navigation"
    tabindex="-1"
    (keydown.escape)="closeMenu()"
  >
    @if (isAuthenticated()) {
      <a
        class="header__mobile-link"
        routerLink="/dashboard"
        routerLinkActive="header__mobile-link--active"
        ariaCurrentWhenActive="page"
        (click)="closeMenu()"
        >Dashboard</a
      >

      @if (isAgent()) {
        <p class="header__mobile-heading">Agent</p>
        @for (link of agentLinks(); track link.path) {
          <a
            class="header__mobile-link"
            [routerLink]="link.path"
            routerLinkActive="header__mobile-link--active"
            ariaCurrentWhenActive="page"
            (click)="closeMenu()"
            >{{ link.label }}</a
          >
        }
      }

      @if (isOps()) {
        <p class="header__mobile-heading">Ops</p>
        @for (link of opsLinks; track link.path) {
          <a
            class="header__mobile-link"
            [routerLink]="link.path"
            routerLinkActive="header__mobile-link--active"
            ariaCurrentWhenActive="page"
            (click)="closeMenu()"
            >{{ link.label }}</a
          >
        }
      }

      @if (isAdmin()) {
        <p class="header__mobile-heading">Admin</p>
        @for (link of adminLinks; track link.path) {
          <a
            class="header__mobile-link"
            [routerLink]="link.path"
            routerLinkActive="header__mobile-link--active"
            ariaCurrentWhenActive="page"
            (click)="closeMenu()"
            >{{ link.label }}</a
          >
        }
      }

      <p class="header__mobile-heading">{{ displayName() }}</p>
      <a
        class="header__mobile-link"
        routerLink="/profile"
        routerLinkActive="header__mobile-link--active"
        ariaCurrentWhenActive="page"
        (click)="closeMenu()"
        >Profile</a
      >
      <button
        class="header__mobile-link header__mobile-link--action"
        type="button"
        (click)="logout()"
      >
        Log out
      </button>
    } @else {
      <a class="header__mobile-link" routerLink="/auth/login" (click)="closeMenu()">Log in</a>
      <a class="header__mobile-link" routerLink="/auth/register" (click)="closeMenu()">Sign up</a>
    }
    @if (isDebugEnabled) {
      <p class="header__mobile-heading">Debug · Switch User</p>
      @for (g of devUserGroups(); track g.group) {
        <p class="header__mobile-subheading">{{ g.heading }}</p>
        @for (u of g.users; track u.email) {
          <button
            class="header__mobile-link header__mobile-link--action"
            type="button"
            (click)="switchUser(u.email)"
          >
            {{ u.first_name }} {{ u.last_name }} ({{ u.role }})
          </button>
        }
      } @empty {
        <button
          class="header__mobile-link header__mobile-link--action"
          type="button"
          (click)="toggleDebug()"
        >
          Load users…
        </button>
      }
    }
    <button
      class="header__mobile-link header__mobile-theme-toggle"
      type="button"
      (click)="toggleTheme()"
    >
      @if (isDark()) {
        <app-icon name="sun" [size]="16" />
        Light mode
      } @else {
        <app-icon name="moon" [size]="16" />
        Dark mode
      }
    </button>
  </nav>
</header>
